import React, { useEffect, useMemo, useState } from 'react';
import {
  Area, ComposedChart, Line, ReferenceArea, ReferenceDot, ReferenceLine,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';

// The evidence on the card. One series, drawn the quiet way: a 2px gold line
// (gold is "you" everywhere in the product), a hairline at your usual level, a
// faint wash for your normal day-to-day band, and ONE loud mark — the day or
// the fortnight the card is about — in the valence colour (green better, red
// worse, gold when the metric has no better direction). Text never wears the
// data colour; the headline above the chart carries the words.
//
// Honesty replaces scarcity as the trust mechanism now, so this is not
// decoration: a weak observation has to *look* weak, and nothing shows "it
// barely cleared the band" like drawing the band.

// Gold is the one job colour; everything else reads the theme tokens so the
// chart flips with the page.
const GOLD = 'var(--you)';
const GOOD = { light: '#059669', dark: '#10B981' };
const OFF = { light: '#C6432F', dark: '#E2604F' };
const GRID = 'var(--fg-3)';
const PAPER = 'var(--bg-raised)';

export const useIsDark = () => {
  const read = () =>
    typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
  const [dark, setDark] = useState(read);
  useEffect(() => {
    const el = document.documentElement;
    const obs = new MutationObserver(() => setDark(read()));
    obs.observe(el, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);
  return dark;
};

export const valenceColor = (valence, dark) => {
  if (valence === 'better') return dark ? GOOD.dark : GOOD.light;
  if (valence === 'worse') return dark ? OFF.dark : OFF.light;
  return GOLD;
};

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const fmtValue = (v, unit) => {
  if (v == null) return '—';
  const n = Math.abs(v) >= 20 || Number.isInteger(v) ? Math.round(v) : Math.round(v * 10) / 10;
  if (!unit) return `${n}`;
  if (unit === '%' || unit === 'Δ' || unit.startsWith('/')) return `${n}${unit}`;
  return `${n} ${unit}`;
};

const TooltipBox = ({ active, payload, unit, dark }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0]?.payload;
  if (!p) return null;
  return (
    <div
      className="rounded-lg border border-line-strong bg-elev px-2.5 py-1.5 text-sm text-fg"
    >
      <span className="font-medium">{fmtValue(p.value, unit)}</span>
      <span className="ml-2 opacity-70">{fmtDate(p.date)}</span>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Series: a day or a window against the person's own level
// ---------------------------------------------------------------------------

const SeriesSparkline = ({ chart, valence, height, quiet }) => {
  const dark = useIsDark();
  const rawPoints = chart.points;
  const points = useMemo(() => rawPoints || [], [rawPoints]);
  const { band, baseline, highlight, unit } = chart;
  const mark = valenceColor(valence, dark);
  const grid = GRID;

  // The wash is a range area ([lo, hi] per point), not a stacked pair: a
  // stack is anchored at zero and drags the y-domain down with it, which
  // flattens a resting heart rate of 64–72 into a line along the top.
  const data = useMemo(() => points.map((p) => ({
    ...p,
    bandRange: band ? [band[0], band[1]] : undefined,
  })), [points, band]);

  const domain = useMemo(() => {
    const vals = points.map((p) => p.value).filter((v) => v != null);
    if (band) vals.push(band[0], band[1]);
    if (baseline != null) vals.push(baseline);
    if (!vals.length) return ['auto', 'auto'];
    const lo = Math.min(...vals);
    const hi = Math.max(...vals);
    const pad = (hi - lo || Math.abs(hi) || 1) * 0.15;
    return [lo - pad, hi + pad];
  }, [points, band, baseline]);

  if (!points.length) {
    return (
      <p className="text-sm text-fg-3">
        No chart for this one — the numbers are in the detail.
      </p>
    );
  }

  // Holes are dropped from the series, so a window edge may fall on a day
  // with no point. Recharts draws a ReferenceArea only between x values that
  // exist in the data, so snap each edge inward to the nearest plotted day.
  const [rawStart, rawEnd] = highlight || [];
  const single = rawStart && rawStart === rawEnd;
  const hStart = rawStart ? (points.find((p) => p.date >= rawStart) || {}).date : undefined;
  const hEnd = rawEnd ? [...points].reverse().find((p) => p.date <= rawEnd)?.date : undefined;
  const highlightPoint = single ? points.find((p) => p.date === rawStart) : null;

  return (
    <div style={{ height }} className="w-full">
      <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 320, height }}>
        <ComposedChart data={data} margin={{ top: 6, right: 8, bottom: 2, left: 8 }}>
          <XAxis dataKey="date" hide />
          <YAxis domain={domain} hide />
          {!quiet && (
            <Tooltip
              content={<TooltipBox unit={unit} dark={dark} />}
              cursor={{ stroke: grid, strokeWidth: 1 }}
              isAnimationActive={false}
            />
          )}
          {band && (
            <Area dataKey="bandRange" stroke="none" fill={GOLD} fillOpacity={0.12}
                  isAnimationActive={false} activeDot={false} />
          )}
          {baseline != null && (
            <ReferenceLine y={baseline} stroke={grid} strokeWidth={1} strokeDasharray="4 4" />
          )}
          {!single && hStart && hEnd && (
            <>
              {/* The window: a soft wash, a hairline where it starts, and a
                  dashed line at the window's own level so before/after read
                  as two steps rather than one box. */}
              <ReferenceArea x1={hStart} x2={hEnd} fill={mark} fillOpacity={0.07} stroke="none" />
              <ReferenceLine x={hStart} stroke="var(--line-strong)" strokeWidth={1} />
              {chart.recent != null && (
                <ReferenceLine
                  segment={[{ x: hStart, y: chart.recent }, { x: hEnd, y: chart.recent }]}
                  stroke={grid} strokeWidth={1} strokeDasharray="4 4"
                />
              )}
            </>
          )}
          <Line
            type="monotone"
            dataKey="value"
            stroke={GOLD}
            strokeWidth={2}
            dot={false}
            connectNulls
            isAnimationActive={false}
            activeDot={quiet ? false : { r: 4, fill: GOLD, stroke: PAPER, strokeWidth: 2 }}
          />
          {highlightPoint && (
            <ReferenceDot
              x={highlightPoint.date}
              y={highlightPoint.value}
              r={5}
              fill={mark}
              stroke={PAPER}
              strokeWidth={2}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Groups: your outcome on days with more vs less of the input (a tendency)
// ---------------------------------------------------------------------------

const GroupsSparkline = ({ chart }) => {
  const groups = chart.groups || [];
  const max = Math.max(...groups.map((g) => Math.abs(g.value || 0)), 1e-9);
  const track = 'var(--line)';
  // Stacked rather than side-by-side so it survives a narrow card: label and
  // value on one line, the bar underneath, both bars sharing one scale.
  return (
    <div className="space-y-2">
      {groups.map((g, i) => (
        <div key={g.key}>
          <div className="flex items-baseline justify-between gap-3">
            <span className="min-w-0 truncate text-sm text-fg-2">
              {g.label}
            </span>
            <span className="shrink-0 text-sm font-medium text-fg">
              {fmtValue(g.value, chart.unit)}
            </span>
          </div>
          <div className="mt-1 h-3 w-full rounded-r" style={{ background: track }}>
            <div
              className="h-3 rounded-r"
              style={{
                width: `${Math.max(4, (Math.abs(g.value) / max) * 100)}%`,
                background: GOLD,
                opacity: i === groups.length - 1 ? 1 : 0.55,
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

const Sparkline = ({ chart, valence, height = 72, quiet = false }) => {
  if (!chart) return null;
  if (chart.type === 'groups') return <GroupsSparkline chart={chart} />;
  return <SeriesSparkline chart={chart} valence={valence} height={height} quiet={quiet} />;
};

export default Sparkline;
